import React, { useState, useEffect }  from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCompleteSignIn } from '../../hooks/authhooks';
import FocusGroupForm from '../../molecules/Desktop/FocusGroupForm';
import BusinessTabs from '../../molecules/Desktop/BusinessTabs';
import { BusinessStageType, BusinessTabType } from '../../data/types';
// import { MOCK_FOCUS_GROUPS } from '../../data/focus_groups_sessions';
import { useFocusGroupRegistrationData, useFocusGroupFormattedSessions } from '../../hooks/businesshooks';
import moment from 'moment-timezone';
import { ReactComponent as CalendarIcon } from '../../icons/CalendarIcon.svg';
import { ReactComponent as ClockIcon } from '../../icons/ClockIcon.svg';
import { ReactComponent as UpcomingIcon } from '../../icons/UpcomingIcon.svg';
// import { ReactComponent as LiveIcon } from '../../icons/LiveIcon.svg';
import { ReactComponent as EndedIcon } from '../../icons/EndedIcon.svg';
import { capitalizeWords, formatDuration } from '../../utils/helpers';

type FocusGroupProps = {}

const FocusGroup: React.FC<FocusGroupProps> = () => {
    const communityId = '';
    const { user } = useCompleteSignIn();
    const username = user?.username || '';
    const uid = user?.uid || '';
    const isLoggedIn = !!username && !!uid;
    const { focusGroupData, isLoading: isLoadingData } = useFocusGroupRegistrationData(uid, communityId);

    const [searchParams] = useSearchParams();
    const stage = searchParams.get('stage');
    const [businessStage, setBusinessStage] = useState<BusinessStageType>(stage as BusinessStageType || 'dream');

    const startTab = {
        dream: 'focus-group', 
        build: 'focus-group', 
        scale: 'focus-group'
    } as BusinessTabType


    const [width, setWidth] = useState<number>(window.innerWidth);

    useEffect(() => {
        document.title = 'Focus Group Sessions';
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = width < 1024;


    return (
        <div className="flex flex-col min-h-screen bg-white">
            <div className={`flex flex-col ${isLoggedIn ? '' : `${isMobile ? '' : 'mt-[60px]'}`}`}>
            {isLoggedIn && <BusinessTabs startTab={startTab} businessStage={businessStage} setBusinessStage={setBusinessStage} />}
            {
                (isLoggedIn) ? (
                    <FocusGroupListings />
                ) : (
                    <FocusGroupForm communityId={communityId} focusGroupData={focusGroupData} isLoadingData={isLoadingData} />
                )
            }
            </div>
        </div>
    );
}


// const formatDuration = (minutes: number): string => {
//     if (minutes < 60) {
//         return `${minutes} min`;
//     } else if (minutes < 1440) { // Less than 24 hours
//         const hours = Math.floor(minutes / 60);
//         return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
//     } else if (minutes < 10080) { // Less than 7 days
//         const days = Math.floor(minutes / 1440);
//         return `${days} ${days === 1 ? 'day' : 'days'}`;
//     } else if (minutes < 43200) { // Less than 30 days
//         const weeks = Math.floor(minutes / 10080);
//         return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
//     } else { // 30+ days
//         const months = Math.floor(minutes / 43200);
//         return `${months} ${months === 1 ? 'month' : 'months'}`;
//     }
// };

const FocusGroupListings = () => {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState<'all' | 'upcoming' | 'in-progress' | 'ended'>('all');
    const [width, setWidth] = useState<number>(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = width < 1024;

    const filterTabs = [
        { id: 'all', label: 'All Sessions', activeClass: 'bg-primaryDark-0 text-white border-primaryDark-0', inactiveClass: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200' },
        { id: 'upcoming', label: 'Upcoming', activeClass: 'bg-blue-50 text-blue-700 border border-blue-200', inactiveClass: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200' },
        { id: 'in-progress', label: 'Live Now', activeClass: 'bg-green-50 text-green-700 border border-green-200', inactiveClass: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200' },
        { id: 'ended', label: 'Ended', activeClass: 'bg-gray-50 text-gray-700 border border-gray-200', inactiveClass: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200' }
    ] as const;

    const getSessionStatus = (sessionDate: moment.Moment, duration: number) => {
        const now = moment();
        const sessionStart = sessionDate;
        const sessionEnd = moment(sessionStart).add(duration, 'minutes');

        if (now.isAfter(sessionEnd)) {
            return { status: 'ended', label: 'Ended', color: 'gray' };
        } else if (now.isSameOrAfter(sessionStart) && now.isSameOrBefore(sessionEnd)) {
            return { status: 'in-progress', label: 'Live Now', color: 'green' };
        } else {
            return { status: 'upcoming', label: 'Upcoming', color: 'blue' };
        }
    };

    // const communityId = ""
    const { formattedFocusGroupSessions } = useFocusGroupFormattedSessions()

    const filteredGroups = formattedFocusGroupSessions.filter(group => {
        if (activeFilter === 'all') return true;
        const status = getSessionStatus(moment(group.sessionDate), group.duration);
        return status.status === activeFilter;
    });

    return (
        <div className={`${isMobile ? 'max-w-full px-4' : 'max-w-4xl px-6 w-full'} mx-auto py-8`}>
            <div className="mb-8">
                <h1 className={`text-xl font-bold font-raleway text-gray-900`}>Focus Group Sessions</h1>
                <p className="mt-2 text-gray-600">Join our interactive sessions designed to help you grow your business</p>
            </div>

            {/* Filter Tabs */}
            <div className="mb-8">
                <div className={`flex ${isMobile ? 'gap-2' : 'space-x-4'} overflow-x-auto pb-2 ${isMobile ? 'scrollbar-hide' : ''}`}>
                    {filterTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveFilter(tab.id)}
                            className={`${isMobile ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'} rounded-lg font-medium whitespace-nowrap transition-colors ${
                                activeFilter === tab.id ? tab.activeClass : tab.inactiveClass
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="space-y-6">
                {filteredGroups.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">No sessions found</p>
                    </div>
                ) : (
                    filteredGroups.map((group) => {
                        const status = getSessionStatus(moment(group.sessionDate), group.duration);
                        return (
                            <div key={group.id} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-200">
                                <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
                                    <div className={`${isMobile ? 'flex flex-col space-y-4' : 'flex items-center justify-between'} mb-6`}>
                                        <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'items-center space-x-4'}`}>
                                            <img src={group.orgLogo} alt={group.orgName} className={`${isMobile ? 'w-12 h-9' : 'w-16 h-12'} object-contain`} />
                                            <div>
                                                <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-900`}>{group.title}</h2>
                                                <p className="text-gray-600">{group.orgName} {group.sessionType ? capitalizeWords(group.sessionType.split('_').join(' ')) : 'Focus Group'}</p>
                                            </div>
                                        </div>
                                        <div className={`flex items-center space-x-2 bg-${status.color}-50 px-3 py-1.5 rounded-full border border-${status.color}-100 ${isMobile ? 'self-start' : ''}`}>
                                            {status.status === 'upcoming' && (
                                                <UpcomingIcon className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-${status.color}-600`} />
                                            )}
                                            {status.status === 'in-progress' && (
                                                <div className="relative">
                                                    <div className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} bg-green-500 rounded-full`}></div>
                                                    <div className={`absolute inset-0 ${isMobile ? 'w-2 h-2' : 'w-3 h-3'} bg-green-500 rounded-full animate-ping`}></div>
                                                </div>
                                                // <LiveIcon className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-${status.color}-600`} />
                                            )}
                                            {status.status === 'ended' && (
                                                <EndedIcon className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-${status.color}-600`} />
                                            )}
                                            <span className={`text-sm font-medium text-${status.color}-700`}>{status.label}</span>
                                        </div>
                                    </div>

                                    <p className={`text-gray-600 mb-6 ${isMobile ? 'text-sm' : ''}`}>{group.description}</p>

                                    <div className={`${isMobile ? 'flex flex-col space-y-4' : 'flex items-center justify-between'}`}>
                                        <div className="space-y-2">
                                            <div className="flex items-center text-gray-700">
                                                <CalendarIcon className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400 mr-2`} />
                                                <span className={`${isMobile ? 'text-sm' : ''}`}>{moment(group.sessionDate).format('dddd, MMMM D, YYYY')}</span>
                                            </div>
                                            <div className="flex items-center text-gray-700">
                                                <ClockIcon className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400 mr-2`} />
                                                <span className={`${isMobile ? 'text-sm' : ''} font-medium`}>
                                                    {moment(group.sessionDate).format('h:mm A')}
                                                    <span className="text-gray-400 ml-1 font-normal">
                                                        ({moment(group.sessionDate).tz(moment.tz.guess()).format('z')})
                                                    </span>
                                                    <span className="text-gray-400 ml-1 font-normal">
                                                        • {formatDuration(group.duration)}
                                                    </span>
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => navigate(`/focus-group/${group.id}`)}
                                            className={`${isMobile ? 'w-full mt-4' : ''} bg-primaryDark-0 hover:bg-primaryDark-100 text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-200`}
                                        >
                                            {status.status !== 'in-progress' ? 
                                            `View ${group.sessionType  === 'survey' ? '' : 'Session'}` 
                                            : `Join ${group.sessionType  === 'survey' ? '' : 'Session'}`}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default FocusGroup;